import 'keen-slider/keen-slider.min.css';
import { useEffect, useRef } from 'react';
import { useKeenSlider } from 'keen-slider/react';
import { Icon, IconColor, IconID } from '@common/Icon/Icon';

import { KeyboardKeyName } from '@models/data';

import { useIsomorphicLayoutEffect } from '@hooks/useIsomorphicLayoutEffect';
import type { AlternativeProductProps } from '@product/AlternativeProduct/AlternativeProduct';
import { AlternativeProduct } from '@product/AlternativeProduct/AlternativeProduct';
import type { ActiveBreakpoints } from '@hooks/useBreakpoints';
import { useBreakpoints } from '@hooks/useBreakpoints';

export interface ProductsCarouselProps {
  activeIndex: number;
  products: AlternativeProductProps[];
}

const styles = `
  .keen-slider {
    display: flex;
    overflow: hidden;
  }

  .keen-slider__slide {
    padding: 10px;
    flex-shrink: 0;
    transform: translate3d(0px, 0px, 0px)
  }

  @media (min-width: 940px) {
    .keen-slider__slide {
      min-width: 25%;
      max-width: 25%;
    }
  }

  @media (max-width: 939px) and (min-width: 720px) {
    .keen-slider__slide {
      min-width: 33%;
      max-width: 33%;
    }
  }

  @media (max-width: 719px) and (min-width: 536px) {
    .keen-slider__slide {
      min-width: 50%;
      max-width: 50%;
    }
  }

  @media (max-width: 535px) {
    .keen-slider__slide {
      min-width: 100%;
      max-width: 100%;
    }
  }
`;

const getSlidesPerView = (breakpoint: ActiveBreakpoints) => {
  if (breakpoint.lg) {
    return 4;
  }
  if (breakpoint.md) {
    return 3;
  }
  if (breakpoint.sm) {
    return 2;
  }
  return 1;
};

export const ProductsCarousel = ({ activeIndex, products }: ProductsCarouselProps) => {
  const isMountedRef = useRef<boolean>(false);
  const breakpoint = useBreakpoints();
  const [carouselRef, carousel] = useKeenSlider<HTMLDivElement>({
    initial: activeIndex,
    slidesPerView: getSlidesPerView(breakpoint),
    loop: false,
    spacing: 0,
    mounted: s => {
      const keyboardListener = (e: KeyboardEvent) => {
        if (e.key === KeyboardKeyName.ARROW_LEFT || e.key === KeyboardKeyName.IE_ARROW_LEFT) {
          s.prev();
        } else if (e.key === KeyboardKeyName.ARROW_RIGHT || e.key === KeyboardKeyName.IE_ARROW_RIGHT) {
          s.next();
        }
      };

      // remove event listener if it has been added before
      window.removeEventListener('keyup', keyboardListener);
      window.addEventListener('keyup', keyboardListener);
    },
    slideChanged: s => {
      if (
        !isMountedRef.current ||
        Number.isNaN(s.details().relativeSlide) ||
        s.details().relativeSlide === activeIndex
      ) {
        return true;
      }
      return undefined;
    },
  });

  useEffect(() => {
    if (carousel && carousel.details().relativeSlide !== activeIndex) {
      carousel.moveToSlideRelative(activeIndex, true);
    }
  }, [activeIndex, carousel]);

  useEffect(() => {
    if (carousel) {
      carousel.refresh();
    }
  }, [products, carousel]);

  useIsomorphicLayoutEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const noBreakpoint = !breakpoint.lg && !breakpoint.md && !breakpoint.xl && !breakpoint.sm;

  return (
    <div data-testid="products-carousel" className="w-full pl-20 pr-20">
      <style type="text/css">{styles}</style>
      <div className="relative m-auto">
        {((products.length > 4 && breakpoint.lg && !breakpoint.xl) ||
          (products.length > 3 && breakpoint.md && !breakpoint.lg) ||
          (products.length > 1 && breakpoint.sm && !breakpoint.md) ||
          (products.length > 1 && noBreakpoint)) && (
          <>
            <div
              data-testid="products-carousel-modal-prev"
              className="absolute cursor-pointer top-1/2 -translate-y-1/2 -left-20"
              onClick={() => carousel.prev()}
            >
              <Icon size={16} id={IconID.ARROW_LEFT} color={IconColor.LIGHT_GRAY} title="Previous" />
            </div>
            <div
              data-testid="products-carousel-modal-next"
              className="absolute cursor-pointer top-1/2 -translate-y-1/2 -right-20"
              onClick={() => carousel.next()}
            >
              <Icon size={16} id={IconID.ARROW_RIGHT} color={IconColor.LIGHT_GRAY} title="Next" />
            </div>
          </>
        )}
        <div ref={carouselRef} className="keen-slider mt-8" data-testid="products-carousel-swipe">
          {products.map((product, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className={`keen-slider__slide flex number-slide${idx + 1}`} key={`carousel-product-${idx}`}>
              <AlternativeProduct
                title={product.title}
                slug={product.slug}
                singleUsp={product.singleUsp}
                new={product.new}
                image={product.image}
                priceType={product.priceType}
                prices={product.prices}
                deliveryTimes={product.deliveryTimes}
                productIdDisplayName={product.productIdDisplayName}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
